import { HelpLinkButton } from 'app/modules/fincenSAR/components/StylingUtils';
import { FINTRAC_STR_STARTING_ACTION_ACCOUNT_INFORMATION_FIELDS } from 'app/modules/fintracStr/formConfig/transactions/startingActions/details/account';
import {
  FINTRAC_STR_STARTING_ACTION_DIRECTION_OPTIONS,
  FINTRAC_STR_OUT_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE_OPTIONS,
  FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE_OPTIONS,
  FINTRAC_STR_CURRENCY_CODES_OPTIONS,
  FINTRAC_STR_VIRTUAL_CURRENCY_TYPE_CODE_OPTIONS,
  FINTRAC_STR_ACCOUNT_STATUS_AT_TIME_OF_TRANSACTION_OPTIONS,
  FINTRAC_STR_YES_NO_OPTIONS,
  FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE,
  FINTRAC_STR_OUT_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE,
  FINTRAC_STR_VIRTUAL_CURRENCY_TYPE_CODE,
} from 'app/modules/fintracStr/formConstants';
import { getDependentPaths } from 'app/modules/fintracStr/formUtils';
import { FintracFormSingleField } from 'app/modules/fintracStr/models';
import { isFieldBlank } from 'app/modules/fintracStr/renderConditions';
import {
  U21FormFieldTypes,
  U21SelectOptionProps,
} from 'app/shared/u21-ui/components';
import { isUndefined } from 'lodash';

export const FINTRAC_STR_TRANSACTION_STARTING_ACTIONS_DETAILS_FIELDS: FintracFormSingleField =
  {
    type: 'section',
    repeatable: false,
    label: 'Details',
    name: 'details',
    mustExistInPayload: true,
    mustHaveValueIfExist: true,
    isSectionWrapperHidden: true,
    fields: [
      {
        type: U21FormFieldTypes.RADIO_GROUP,
        name: 'direction',
        label: 'Direction of starting action',
        required: true,
        mustExistInPayload: false,
        mustHaveValueIfExist: true,
        fieldProps: {
          options: FINTRAC_STR_STARTING_ACTION_DIRECTION_OPTIONS,
          horizontal: true,
        },
        defaultValue: 1,
        help: (
          <p>
            Refer to the STR guidance on
            <HelpLinkButton
              href="https://fintrac-canafe.canada.ca/"
              variant="text"
              color="primary"
            >
              FINTRAC&apos;s website
            </HelpLinkButton>{' '}
            for more information on determining the direction of the starting
            action
          </p>
        ),
      },
      [
        {
          type: U21FormFieldTypes.SELECT,
          name: 'fundAssetVirtualCurrencyTypeCode',
          label: 'Type of funds, assets or virtual currency',
          dependentPaths: (sectionIndex, fieldSectionIndex) =>
            getDependentPaths(
              undefined,
              fieldSectionIndex && fieldSectionIndex.length === 1
                ? [
                    `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].details.direction`,
                  ]
                : undefined,
              sectionIndex,
              fieldSectionIndex,
            ),
          fieldProps: (
            flattenedValues,
            _,
            sectionIndex,
            fieldSectionIndex,
          ) => ({
            options: ((): U21SelectOptionProps<number>[] => {
              if (isUndefined(fieldSectionIndex) || isUndefined(sectionIndex)) {
                return FINTRAC_STR_OUT_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE_OPTIONS;
              }

              return flattenedValues[
                `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].details.direction`
              ] === 1
                ? FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE_OPTIONS
                : FINTRAC_STR_OUT_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE_OPTIONS;
            })(),
          }),
          required: true,
          mustExistInPayload: false,
          mustHaveValueIfExist: true,
        },
        {
          type: U21FormFieldTypes.TEXT,
          name: 'fundAssetVirtualCurrencyTypeOther',
          label: 'Other type of funds, assets or virtual currency description',
          dependentPaths: (sectionIndex, fieldSectionIndex) =>
            getDependentPaths(
              undefined,
              fieldSectionIndex && fieldSectionIndex.length === 1
                ? [
                    `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].details.fundAssetVirtualCurrencyTypeCode`,
                  ]
                : undefined,
              sectionIndex,
              fieldSectionIndex,
            ),
          hideIfTrue: {
            and: [
              {
                path: (sectionIndex, fieldSectionIndex) =>
                  fieldSectionIndex && fieldSectionIndex.length === 1
                    ? `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].details.fundAssetVirtualCurrencyTypeCode`
                    : '',
                conditions: (value) =>
                  value !==
                  FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE.OTHER
                    .value,
              },
            ],
          },
          mustExistInPayload: false,
          mustHaveValueIfExist: false,
        },
      ],
      {
        type: U21FormFieldTypes.TEXT,
        name: 'amount',
        label: 'Amount',
        mustExistInPayload: false,
        mustHaveValueIfExist: true,
      },
      {
        type: U21FormFieldTypes.SELECT,
        name: 'currencyCode',
        label: 'Currency code',
        fieldProps: {
          options: FINTRAC_STR_CURRENCY_CODES_OPTIONS,
        },
        mustExistInPayload: false,
        mustHaveValueIfExist: true,
        dependentPaths: (sectionIndex, fieldSectionIndex) =>
          getDependentPaths(
            undefined,
            fieldSectionIndex && fieldSectionIndex.length === 1
              ? [
                  `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].details.fundAssetVirtualCurrencyTypeCode`,
                ]
              : undefined,
            sectionIndex,
            fieldSectionIndex,
          ),
        hideIfTrue: {
          or: [
            {
              path: (sectionIndex, fieldSectionIndex) =>
                fieldSectionIndex && fieldSectionIndex.length === 1
                  ? `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].details.fundAssetVirtualCurrencyTypeCode`
                  : '',
              conditions: (value) =>
                value ===
                FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE
                  .VIRTUAL_CURRENCY.value,
            },
          ],
        },
      },
      [
        {
          type: U21FormFieldTypes.SELECT,
          name: 'virtualCurrencyTypeCode',
          label: 'Virtual currency type',
          fieldProps: (
            flattenedValues,
            mutators,
            sectionIndex,
            fieldSectionIndex,
          ) => ({
            options: FINTRAC_STR_VIRTUAL_CURRENCY_TYPE_CODE_OPTIONS,
            onChange: () => {
              if (isUndefined(fieldSectionIndex) || isUndefined(sectionIndex)) {
                return;
              }

              const keys = [
                `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].details.virtualCurrencyTransactionIds`,
                `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].details.receivingVirtualCurrencyAddresses`,
                `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].details.sendingVirtualCurrencyAddresses`,
              ];

              keys.forEach((key) => {
                if (isFieldBlank(flattenedValues[key])) {
                  mutators.setFieldValueUntyped(key, ['']);
                }
              });
            },
          }),
          mustExistInPayload: false,
          mustHaveValueIfExist: true,
          dependentPaths: (sectionIndex, fieldSectionIndex) =>
            getDependentPaths(
              undefined,
              fieldSectionIndex && fieldSectionIndex.length === 1
                ? [
                    `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].details.fundAssetVirtualCurrencyTypeCode`,
                    `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].details.virtualCurrencyTransactionIds`,
                    `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].details.receivingVirtualCurrencyAddresses`,
                    `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].details.sendingVirtualCurrencyAddresses`,
                  ]
                : undefined,
              sectionIndex,
              fieldSectionIndex,
            ),
          hideIfTrue: {
            or: [
              {
                path: (sectionIndex, fieldSectionIndex) =>
                  fieldSectionIndex && fieldSectionIndex.length === 1
                    ? `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].details.fundAssetVirtualCurrencyTypeCode`
                    : '',
                conditions: (value) =>
                  value ===
                    FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE
                      .BANK_DRAFT.value ||
                  value ===
                    FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE.CASH
                      .value ||
                  value ===
                    FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE.CHEQUE
                      .value ||
                  value ===
                    FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE
                      .DOMESTIC_FUNDS_TRANSFER.value ||
                  value ===
                    FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE
                      .EMAIL_MONEY_TRANSFER.value ||
                  value ===
                    FINTRAC_STR_OUT_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE
                      .FUNDS_WITHDRAWAL.value ||
                  value ===
                    FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE
                      .INTERNATIONAL_FUNDS_TRANSFER.value ||
                  value ===
                    FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE
                      .JEWELLERY.value ||
                  value ===
                    FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE
                      .MOBILE_MONEY_TRANSFER.value ||
                  value ===
                    FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE
                      .MONEY_ORDER.value ||
                  value ===
                    FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE
                      .PRECIOUS_METALS.value ||
                  value ===
                    FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE
                      .PRECIOUS_STONES.value,
              },
            ],
          },
        },
        {
          type: U21FormFieldTypes.TEXT,
          name: 'virtualCurrencyTypeOther',
          label: 'Other virtual currency type description',
          dependentPaths: (sectionIndex, fieldSectionIndex) =>
            getDependentPaths(
              undefined,
              fieldSectionIndex && fieldSectionIndex.length === 1
                ? [
                    `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].details.virtualCurrencyTypeCode`,
                    `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].details.account.virtualCurrencyTypeCode`,
                  ]
                : undefined,
              sectionIndex,
              fieldSectionIndex,
            ),
          hideIfTrue: {
            and: [
              {
                path: (sectionIndex, fieldSectionIndex) =>
                  fieldSectionIndex && fieldSectionIndex.length === 1
                    ? `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].details.virtualCurrencyTypeCode`
                    : '',
                conditions: (value) =>
                  value !== FINTRAC_STR_VIRTUAL_CURRENCY_TYPE_CODE.OTHER.value,
              },
              {
                path: (sectionIndex, fieldSectionIndex) =>
                  fieldSectionIndex && fieldSectionIndex.length === 1
                    ? `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].details.account.virtualCurrencyTypeCode`
                    : '',
                conditions: (value) =>
                  value !== FINTRAC_STR_VIRTUAL_CURRENCY_TYPE_CODE.OTHER.value,
              },
            ],
          },
          mustExistInPayload: false,
          mustHaveValueIfExist: false,
        },
      ],
      {
        type: U21FormFieldTypes.TEXT,
        name: 'exchangeRate',
        label: 'Exchange rate',
        mustExistInPayload: false,
        mustHaveValueIfExist: true,
      },
      {
        type: U21FormFieldTypes.TEXT,
        name: 'virtualCurrencyTransactionIds',
        label: 'Virtual currency transaction IDs',
        repeatable: true,
        mustExistInPayload: true,
        mustHaveValueIfExist: false,
        defaultValue: '',
        required: (flattenedValues, sectionIndex, fieldSectionIndex) => {
          if (!isUndefined(sectionIndex) && !isUndefined(fieldSectionIndex)) {
            return !isFieldBlank(
              flattenedValues[
                `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].details.virtualCurrencyTypeCode`
              ],
            );
          }

          return false;
        },
        defaultNumOfEntries: (required) => (required ? 1 : 0),
        dependentPaths: (sectionIndex, fieldSectionIndex) =>
          getDependentPaths(
            undefined,
            fieldSectionIndex && fieldSectionIndex.length === 1
              ? [
                  `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].details.fundAssetVirtualCurrencyTypeCode`,
                  `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].details.virtualCurrencyTypeCode`,
                ]
              : undefined,
            sectionIndex,
            fieldSectionIndex,
          ),
        hideIfTrue: {
          or: [
            {
              path: (sectionIndex, fieldSectionIndex) =>
                fieldSectionIndex && fieldSectionIndex.length === 1
                  ? `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].details.fundAssetVirtualCurrencyTypeCode`
                  : '',
              conditions: (value) =>
                value ===
                  FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE
                    .BANK_DRAFT.value ||
                value ===
                  FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE.CASH
                    .value ||
                value ===
                  FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE.CHEQUE
                    .value ||
                value ===
                  FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE
                    .DOMESTIC_FUNDS_TRANSFER.value ||
                value ===
                  FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE
                    .EMAIL_MONEY_TRANSFER.value ||
                value ===
                  FINTRAC_STR_OUT_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE
                    .FUNDS_WITHDRAWAL.value ||
                value ===
                  FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE
                    .INTERNATIONAL_FUNDS_TRANSFER.value ||
                value ===
                  FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE.JEWELLERY
                    .value ||
                value ===
                  FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE
                    .MOBILE_MONEY_TRANSFER.value ||
                value ===
                  FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE
                    .MONEY_ORDER.value ||
                value ===
                  FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE
                    .PRECIOUS_METALS.value ||
                value ===
                  FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE
                    .PRECIOUS_STONES.value,
            },
            {
              path: (sectionIndex, fieldSectionIndex) =>
                fieldSectionIndex && fieldSectionIndex.length === 1
                  ? `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].details.virtualCurrencyTypeCode`
                  : '',
              conditions: (value) => isFieldBlank(value),
            },
          ],
        },
      },
      {
        type: U21FormFieldTypes.TEXT,
        name: 'sendingVirtualCurrencyAddresses',
        label: 'Sending virtual currency address',
        repeatable: true,
        mustExistInPayload: true,
        mustHaveValueIfExist: false,
        defaultValue: '',
        required: (flattenedValues, sectionIndex, fieldSectionIndex) => {
          if (!isUndefined(sectionIndex) && !isUndefined(fieldSectionIndex)) {
            return (
              !isFieldBlank(
                flattenedValues[
                  `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].details.virtualCurrencyTypeCode`
                ],
              ) &&
              isFieldBlank(
                flattenedValues[
                  `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].details.receivingVirtualCurrencyAddresses`
                ],
              )
            );
          }

          return false;
        },
        defaultNumOfEntries: (required) => (required ? 1 : 0),
        dependentPaths: (sectionIndex, fieldSectionIndex) =>
          getDependentPaths(
            undefined,
            fieldSectionIndex && fieldSectionIndex.length === 1
              ? [
                  `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].details.fundAssetVirtualCurrencyTypeCode`,
                  `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].details.virtualCurrencyTypeCode`,
                  `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].details.receivingVirtualCurrencyAddresses`,
                ]
              : undefined,
            sectionIndex,
            fieldSectionIndex,
          ),
        hideIfTrue: {
          or: [
            {
              path: (sectionIndex, fieldSectionIndex) =>
                fieldSectionIndex && fieldSectionIndex.length === 1
                  ? `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].details.fundAssetVirtualCurrencyTypeCode`
                  : '',
              conditions: (value) =>
                value ===
                  FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE
                    .BANK_DRAFT.value ||
                value ===
                  FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE.CASH
                    .value ||
                value ===
                  FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE.CHEQUE
                    .value ||
                value ===
                  FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE
                    .DOMESTIC_FUNDS_TRANSFER.value ||
                value ===
                  FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE
                    .EMAIL_MONEY_TRANSFER.value ||
                value ===
                  FINTRAC_STR_OUT_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE
                    .FUNDS_WITHDRAWAL.value ||
                value ===
                  FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE
                    .INTERNATIONAL_FUNDS_TRANSFER.value ||
                value ===
                  FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE.JEWELLERY
                    .value ||
                value ===
                  FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE
                    .MOBILE_MONEY_TRANSFER.value ||
                value ===
                  FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE
                    .MONEY_ORDER.value ||
                value ===
                  FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE
                    .PRECIOUS_METALS.value ||
                value ===
                  FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE
                    .PRECIOUS_STONES.value,
            },
            {
              path: (sectionIndex, fieldSectionIndex) =>
                fieldSectionIndex && fieldSectionIndex.length === 1
                  ? `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].details.virtualCurrencyTypeCode`
                  : '',
              conditions: (value) => isFieldBlank(value),
            },
          ],
        },
      },
      {
        type: U21FormFieldTypes.TEXT,
        name: 'receivingVirtualCurrencyAddresses',
        label: 'Receiving virtual currency address',
        required: (flattenedValues, sectionIndex, fieldSectionIndex) => {
          if (!isUndefined(sectionIndex) && !isUndefined(fieldSectionIndex)) {
            return (
              !isFieldBlank(
                flattenedValues[
                  `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].details.virtualCurrencyTypeCode`
                ],
              ) &&
              isFieldBlank(
                flattenedValues[
                  `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].details.sendingVirtualCurrencyAddresses`
                ],
              )
            );
          }

          return false;
        },
        defaultNumOfEntries: (required) => (required ? 1 : 0),
        repeatable: true,
        mustExistInPayload: true,
        mustHaveValueIfExist: false,
        defaultValue: '',
        dependentPaths: (sectionIndex, fieldSectionIndex) =>
          getDependentPaths(
            undefined,
            fieldSectionIndex && fieldSectionIndex.length === 1
              ? [
                  `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].details.fundAssetVirtualCurrencyTypeCode`,
                  `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].details.virtualCurrencyTypeCode`,
                  `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].details.sendingVirtualCurrencyAddresses`,
                ]
              : undefined,
            sectionIndex,
            fieldSectionIndex,
          ),
        hideIfTrue: {
          or: [
            {
              path: (sectionIndex, fieldSectionIndex) =>
                fieldSectionIndex && fieldSectionIndex.length === 1
                  ? `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].details.fundAssetVirtualCurrencyTypeCode`
                  : '',
              conditions: (value) =>
                value ===
                  FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE
                    .BANK_DRAFT.value ||
                value ===
                  FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE.CASH
                    .value ||
                value ===
                  FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE.CHEQUE
                    .value ||
                value ===
                  FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE
                    .DOMESTIC_FUNDS_TRANSFER.value ||
                value ===
                  FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE
                    .EMAIL_MONEY_TRANSFER.value ||
                value ===
                  FINTRAC_STR_OUT_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE
                    .FUNDS_WITHDRAWAL.value ||
                value ===
                  FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE
                    .INTERNATIONAL_FUNDS_TRANSFER.value ||
                value ===
                  FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE.JEWELLERY
                    .value ||
                value ===
                  FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE
                    .MOBILE_MONEY_TRANSFER.value ||
                value ===
                  FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE
                    .MONEY_ORDER.value ||
                value ===
                  FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE
                    .PRECIOUS_METALS.value ||
                value ===
                  FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE
                    .PRECIOUS_STONES.value,
            },
            {
              path: (sectionIndex, fieldSectionIndex) =>
                fieldSectionIndex && fieldSectionIndex.length === 1
                  ? `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].details.virtualCurrencyTypeCode`
                  : '',
              conditions: (value) => isFieldBlank(value),
            },
          ],
        },
      },
      [
        {
          type: U21FormFieldTypes.TEXT,
          name: 'referenceNumber',
          label: 'Reference number',
          mustExistInPayload: false,
          mustHaveValueIfExist: false,
          help: 'This field is for non-account based sectors and should only be completed if applicable',
        },
        {
          type: U21FormFieldTypes.TEXT,
          name: 'referenceNumberOtherRelatedNumber',
          label: 'Other number related to reference number',
          mustExistInPayload: false,
          mustHaveValueIfExist: false,
        },
      ],
      {
        type: 'section',
        repeatable: false,
        label: 'Account Information',
        name: 'account',
        mustExistInPayload: false,
        mustHaveValueIfExist: true,
        isSectionWrapperHidden: true,
        fields: FINTRAC_STR_STARTING_ACTION_ACCOUNT_INFORMATION_FIELDS,
        dependentPaths: (sectionIndex, fieldSectionIndex) =>
          getDependentPaths(
            undefined,
            fieldSectionIndex && fieldSectionIndex.length === 1
              ? [
                  `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].details.fundAssetVirtualCurrencyTypeCode`,
                ]
              : undefined,
            sectionIndex,
            fieldSectionIndex,
          ),
        hideIfTrue: {
          and: [
            {
              path: (sectionIndex, fieldSectionIndex) =>
                fieldSectionIndex && fieldSectionIndex.length === 1
                  ? `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].details.fundAssetVirtualCurrencyTypeCode`
                  : '',
              conditions: (value) =>
                isFieldBlank(value) ||
                value ===
                  FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE.JEWELLERY
                    .value ||
                value ===
                  FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE
                    .PRECIOUS_METALS.value ||
                value ===
                  FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE
                    .PRECIOUS_STONES.value,
            },
          ],
        },
      },
      {
        type: U21FormFieldTypes.SELECT,
        name: 'accountStatusAtTimeOfTransaction',
        label: 'Account status at time of transaction',
        fieldProps: {
          options: FINTRAC_STR_ACCOUNT_STATUS_AT_TIME_OF_TRANSACTION_OPTIONS,
        },
        mustExistInPayload: false,
        mustHaveValueIfExist: true,
        dependentPaths: (sectionIndex, fieldSectionIndex) =>
          getDependentPaths(
            undefined,
            fieldSectionIndex && fieldSectionIndex.length === 1
              ? [
                  `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].details.fundAssetVirtualCurrencyTypeCode`,
                ]
              : undefined,
            sectionIndex,
            fieldSectionIndex,
          ),
        hideIfTrue: {
          and: [
            {
              path: (sectionIndex, fieldSectionIndex) =>
                fieldSectionIndex && fieldSectionIndex.length === 1
                  ? `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].details.fundAssetVirtualCurrencyTypeCode`
                  : '',
              conditions: (value) =>
                value ===
                  FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE.JEWELLERY
                    .value ||
                value ===
                  FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE
                    .PRECIOUS_METALS.value ||
                value ===
                  FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE
                    .PRECIOUS_STONES.value,
            },
          ],
        },
      },
      {
        type: U21FormFieldTypes.TEXTAREA,
        name: 'howFundsOrVirtualCurrencyObtained',
        label: 'How funds or virtual currency obtained',
        mustExistInPayload: false,
        mustHaveValueIfExist: false,
      },
      {
        type: U21FormFieldTypes.RADIO_GROUP,
        name: 'sourcesOfFundsOrVirtualCurrencyIndicator',
        label:
          'Was information about the source of funds or virtual currency obtained?',
        fieldProps: {
          options: FINTRAC_STR_YES_NO_OPTIONS,
          horizontal: true,
        },
        defaultValue: false,
        required: true,
        mustExistInPayload: false,
        mustHaveValueIfExist: true,
      },
      {
        type: U21FormFieldTypes.RADIO_GROUP,
        name: 'conductorIndicator',
        label:
          'Have you obtained any information about the person or entity conducting or attempting to conduct the transaction?',
        fieldProps: {
          options: FINTRAC_STR_YES_NO_OPTIONS,
          horizontal: true,
        },
        defaultValue: false,
        required: true,
        mustExistInPayload: false,
        mustHaveValueIfExist: true,
      },
    ],
  };
